import React from "react";
import { Document, Page, Text, View, StyleSheet, Font, Image, Link } from "@react-pdf/renderer"; // Import Link
import NuovautoLogo from "../../../assets/nuovauto.png"; // Corrected import path

Font.register({
  family: "Helvetica",
  src: "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.53/fonts/Helvetica.ttf"
});

export interface ServiceOrderItem {
  description: string;
  workingHours: number;
  total: number;
  vehicleType?: string;
  licensePlate?: string;
  chassisNumber?: string;
}

export interface ServiceOrderData {
  orderNumber: string;
  referenceNumber: number;
  date: string;
  customerName: string;
  customerAddress: string;
  customerPhone: string;
  customerEmail: string;
  serviceDescription: string;
  items: ServiceOrderItem[];
  technicianName: string;
  technicianNotes: string;
  signatureCustomer: string;
  signatureTechnician: string;
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyEmail: string;
  companyVAT: string; // Partita IVA

  ///
  logoUrl?: string; // URL or base64 of Mercedes logo
  serviceOrderCode: string;
  barcode: string; // placeholder or image of barcode
  customerTitle: string; // e.g., "Signor"
  licensePlate: string;
  chassisNumber: string;
  engineNumber: string;
  kilometers: string;
  carModel: string;
  customerCode: string;
  technician: string;
  deliveryDate: string;
  deliveryTime: string;
  serviceItems: {
    description: string;
    workingHours: number;
    total: number;
    vehicleType?: string;
    licensePlate?: string;
    chassisNumber?: string;
    createdAt: string;
  }[];
}

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 9,
    padding: 20,
    backgroundColor: "#ffffff",
    color: "#000",
    lineHeight: 1.4
  },
  header: {
    marginBottom: 5,
    marginLeft: 10, // Aligned with borderedSection
    marginRight: 10 // Aligned with borderedSection
  },
  logo: {
    width: 180,
    height: 60,
    alignSelf: "center",
    marginVertical: 0
  },
  flexRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    marginLeft: 10, // Aligned with borderedSection
    marginRight: 10 // Aligned with borderedSection
  },
  section: {
    marginBottom: 10,
    marginLeft: 10, // Aligned with borderedSection
    marginRight: 10 // Aligned with borderedSection
  },
  boldText: {
    fontWeight: "bold"
  },
  sectionTitle: {
    marginBottom: 4,
    fontWeight: "bold",
    fontSize: 10,
    borderBottom: 1,
    paddingBottom: 2
  },
  infoBlocksContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderTopWidth: 1,
    borderColor: "black",
    marginTop: 0,
    marginLeft: 0,
    marginRight: 0,
    marginBottom: 0
  },
  infoBlocksContainerSpacer: {
    marginBottom: 10
  },
  infoBlock: {
    flexBasis: "25%",
    paddingVertical: 2,
    paddingHorizontal: 5,
    borderRightWidth: 1,
    borderColor: "black",
    justifyContent: "center",
    alignItems: "flex-start"
  },
  infoLabel: {
    fontSize: 7,
    color: "#555"
  },
  infoValue: {
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "left"
  },
  itemBlock: {
    marginBottom: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 10 // Matches borderedSection padding
  },
  itemHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 10, // Matches borderedSection padding
    marginBottom: 5,
    borderColor: "black",
    paddingBottom: 2,
    fontWeight: "bold"
  },
  itemDescription: {
    width: 200,
    textAlign: "left"
  },
  itemVehicleType: {
    width: 80,
    textAlign: "center"
  },
  itemLicensePlate: {
    width: 60,
    textAlign: "center"
  },
  itemChassisNumber: {
    width: 100,
    textAlign: "center"
  },
  itemWorkingHours: {
    width: 80,
    textAlign: "right"
  },
  itemPrice: {
    width: 60,
    textAlign: "right"
  },
  borderedSection: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "black",
    marginLeft: 10,
    marginRight: 10,
    padding: 0,
    paddingBottom: 5,
    marginBottom: 10
  },
  totalContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 10,
    paddingRight: 10,
    borderColor: "black",
    paddingTop: 5
  },
  totalText: {
    fontSize: 12,
    fontWeight: "bold"
  },
  signatureRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 40,
    marginLeft: 20,
    marginRight: 20,
    paddingHorizontal: 20
  },
  signatureBox: {
    alignItems: "center",
    width: 200
  },
  signatureLine: {
    width: 150,
    borderBottomWidth: 1,
    borderBottomColor: "black",
    marginBottom: 8
  },
  // New style for links to make them blue and underlined by default
  link: {
    color: "blue",
    textDecoration: "underline"
  }
});

interface ServiceOrderPdfProps {
  data: ServiceOrderData;
}

const ServiceOrderPdf: React.FC<ServiceOrderPdfProps> = ({ data }) => {
  const calculateTotalAmount = () => {
    return data.serviceItems.reduce((sum, item) => sum + item.total, 0).toFixed(2);
  };

  const insuranceName = "-"; // Assuming insurance name is in the first item description

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header with barcode and logo */}
        <Image src={NuovautoLogo} style={styles.logo} />

        {/* Customer & Company Information */}
        <View style={styles.flexRow}>
          <View>
            <Text>
              {data.customerTitle} {data.customerName}
            </Text>
            <Text>{data.customerAddress}</Text>
            <Text>{data.customerPhone}</Text>
            <Text>{data.customerEmail}</Text>
          </View>
          <View>
            {/* Bold text for company name */}
            <Text style={styles.boldText}>NUOVAUTO di Keil Roger</Text>
            <Text>Via San Gottardo 22/A</Text>
            <Text>6532 Arbedo-Castione</Text>
            {/* Link for website */}
            <Link src="http://www.nuovauto.ch" style={styles.link}>
              www.nuovauto.ch
            </Link>
            {/* Link for email */}
            <Link src="mailto:amministrazione@nuovauto.ch" style={styles.link}>
              amministrazione@nuovauto.ch
            </Link>
          </View>
        </View>

        {/* Service Items with continuous borders and Delivery Info */}
        <View style={styles.borderedSection}>
          <View style={styles.infoBlocksContainer}>
            <View style={styles.infoBlock}>
              <Text style={styles.infoLabel}>Ordine di servizio</Text>
              <Text style={styles.infoValue}>{data.referenceNumber}</Text>
            </View>
            <View style={styles.infoBlock}>
              <Text style={styles.infoLabel}>Cliente</Text>
              <Text style={styles.infoValue}>{data.customerName}</Text>
            </View>
            <View style={styles.infoBlock}>
              <Text style={styles.infoLabel}>Data ingresso</Text>
              <Text style={styles.infoValue}>{data.date}</Text>
            </View>
            {/* TODO: fix delivery date */}
            {/* <View style={[styles.infoBlock, { borderRightWidth: 0 }]}>
              <Text style={styles.infoLabel}>Data consegna</Text>
              <Text style={styles.infoValue}>{data.deliveryDate}</Text>
            </View> */}
          </View>
          <View style={styles.infoBlocksContainer}>
            <View style={[styles.infoBlock, { borderRightWidth: 0 }]}>
              <Text style={styles.infoLabel}>Assicurazione</Text>
              <Text style={styles.infoValue}>{insuranceName}</Text>
            </View>
          </View>
          <View style={styles.infoBlocksContainerSpacer} />
          {/* Service Items Header */}
          <View style={styles.itemHeader}>
            <Text style={styles.itemDescription}>Descrizione</Text>
            <Text style={styles.itemVehicleType}>Tipo Veicolo</Text>
            <Text style={styles.itemLicensePlate}>Targa</Text>
            <Text style={styles.itemChassisNumber}>Telaio</Text>
            <Text style={styles.itemWorkingHours}>Ore</Text>
            <Text style={styles.itemPrice}>Prezzo</Text>
          </View>
          {/* Service Items */}
          {data.serviceItems
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
            .map((item, index) => (
              <View key={index} style={styles.itemBlock}>
                <Text style={styles.itemDescription}>{item.description}</Text>
                <Text style={styles.itemVehicleType}>{item.vehicleType || "-"}</Text>
                <Text style={styles.itemLicensePlate}>{item.licensePlate || "-"}</Text>
                <Text style={styles.itemChassisNumber}>{item.chassisNumber || "-"}</Text>
                <Text style={styles.itemWorkingHours}>{item.workingHours}</Text>
                <Text style={styles.itemPrice}> {item.total}</Text>
              </View>
            ))}

          {/* Total */}
          <View style={styles.totalContainer}>
            <Text style={styles.totalText}>Total: {calculateTotalAmount()} CHF</Text>
          </View>
        </View>

        {/* Technician */}
        <View style={styles.section}>
          <Text>Tecnico: {data.technician}</Text>
        </View>

        {/* Signatures */}
        <View style={styles.signatureRow}>
          <View style={styles.signatureBox}>
            <View style={styles.signatureLine} />
            <Text>Firma del Cliente</Text>
          </View>
          <View style={styles.signatureBox}>
            <View style={styles.signatureLine} />
            <Text>Firma Amministrazione</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default ServiceOrderPdf;
